// ============================================
// Stats UI Controller
// Binds to stat bar elements, handles updates,
// status effects, money tick, notifications
// GSAP-powered animations for stat bars & transitions
// ============================================

import { events, Events } from '../engine/events.js';
import { Stats, SPEAKER_DISPLAY_NAMES } from '../engine/constants.js';
import { L } from '../systems/localization.js';
import { storyManager } from '../engine/story.js';

/* global gsap */

/** Stat name → CSS color class mapping */
const STAT_CSS_MAP = {
    [Stats.ENERGY]: 'notif-energy',
    [Stats.AROUSAL]: 'notif-arousal',
    [Stats.FEMININITY]: 'notif-fem',
    [Stats.SUBMISSIVENESS]: 'notif-sub',
    [Stats.MONEY]: 'notif-money',
    [Stats.SHAME]: 'notif-shame',
};

/** Show-Don't-Tell: narrative flavor text per stat (positive / negative) */
const STAT_FLAVOR = {
    [Stats.FEMININITY]: {
        pos: ['It felt... right.', 'Something shifted.', 'A little more you.', 'The mirror agrees.'],
        neg: ['You pulled back.', 'Old habits return.', 'Not yet.'],
    },
    [Stats.SUBMISSIVENESS]: {
        pos: ['You didn\'t even think about saying no.', 'Something in you didn\'t resist.', 'Easier to just... comply.', 'The word "no" felt distant.'],
        neg: ['You stood your ground.', 'Not this time.'],
    },
    [Stats.SHAME]: {
        pos: ['Your cheeks burn.', 'Don\'t think about it.', 'They saw. They all saw.', 'You can\'t take that back.'],
        neg: ['Maybe it\'s okay.', 'The weight lifts, just a little.', 'Breathe.'],
    },
    [Stats.AROUSAL]: {
        pos: ['A warmth spreads through you.', 'Your breath quickens.', 'Focus. Focus.', 'Not here...'],
        neg: ['The tension fades.', 'Clarity returns.'],
    },
};

/** Status effect flag → element ID mapping */
const STATUS_FLAG_MAP = {
    'is_dirty': 'status-dirty',
    'is_drunk': 'status-drunk',
    'is_tired': 'status-tired',
    'has_makeup_on': 'status-makeup',
    'is_visibly_aroused': 'status-aroused',
};

/** HRT mood swing element IDs (driven by int hrt_mood_swing: 0=none, 1=euphoric, 2=weepy, 3=irritable) */
const HRT_MOOD_ICONS = {
    1: 'status-hrt-euphoric',
    2: 'status-hrt-weepy',
    3: 'status-hrt-irritable',
};

/** Compound buff: chastity-fem (visible when BOTH wearing_chastity AND EquippedFeminine are true) */
const COMPOUND_BUFF_FLAGS = {
    'buff-chastity-fem': ['wearing_chastity', 'EquippedFeminine'],
};

class StatsUI {
    constructor() {
        // Element caches
        this._elements = {};
        this._notifContainer = null;
        this._moneyTickTimer = null;
        this._bound = false;

        /** Current stat values (for delta display) */
        this.values = {
            Energy: 100,
            maxEnergy: 100,
            Arousal: 0,
            Femininity: 0,
            Submissiveness: 0,
            Money: 500,
            Shame: 0,
            Day: 1,
            TimeSlot: 0,
        };
    }

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    init() {
        if (this._bound) return;
        this._bound = true;

        this._scope = events.createScope('stats');

        this._cacheElements();
        this._notifContainer = document.getElementById('stat-notifications');
        this._initWeekStripPopover();

        // Suppress notifications during story processing and bulk pushes (prevents +24/-24 sync artifacts)
        this._suppressNotifications = false;
        this._pendingDeltas = {};  // Accumulate stat deltas during suppression

        // Stat context tracking: capture speaker/location for notification context
        this._statContext = '';
        this._scope.on(Events.TAG_CHAR, (speakerId) => {
            if (!speakerId || speakerId === 'info' || speakerId === 'player') return;
            // Resolve display name: strip emotion suffix, look up display name
            const baseKey = speakerId.split('_')[0];
            const displayName = SPEAKER_DISPLAY_NAMES[baseKey];
            if (displayName) {
                this._statContext = displayName;
            } else {
                // Capitalize first letter as fallback
                this._statContext = baseKey.charAt(0).toUpperCase() + baseKey.slice(1);
            }
        });
        this._scope.on(Events.STORY_CLEAR, () => {
            this._suppressNotifications = true;
            // Block ALL stat observer updates during story processing.
            // Ink transiently passes through knots (overnight, sleep, etc.)
            // that change Energy/Money/TimeSlot/Day — we only want FINAL values.
            this._blockStatUpdates = true;
        });
        this._scope.on(Events.STORY_CHOICES_READY, () => {
            // Delay unsuppress slightly so post-choice stat recalculations don't trigger notifications
            setTimeout(() => {
                this._suppressNotifications = false;
                // Flush queued notifications (accumulated during story processing)
                for (const [stat, delta] of Object.entries(this._pendingDeltas)) {
                    if (delta !== 0) this.showNotification(stat, delta);
                }
                this._pendingDeltas = {};
            }, 100);
            this._forceSyncAllFromInk();
            // Re-render week strip — GF state / RentDueDay / weekend_event_pending
            // are not all observed; choices-ready is a safe re-sync point.
            if (this.values && this.values.Day) this._renderWeekStrip(this.values.Day);
        });

        // Also sync after CONTENT_FINISHED (fires for location maps which don't emit CHOICES_READY)
        this._scope.on(Events.STORY_CONTENT_FINISHED, () => {
            this._forceSyncAllFromInk();
        });

        // Bulk sync done — read all stats from Ink and refresh bars silently
        this._scope.on(Events.VARIABLE_CHANGED, (name, value) => {
            if (name === '_bulkSyncDone') {
                this._syncFromInk();
            }
        });

        // Listen for stat changes (from Ink observers during normal gameplay)
        this._scope.on(Events.STAT_CHANGED, (name, newVal) => {
            // Block ALL transient stat changes during story processing
            if (this._blockStatUpdates) return;
            // max_energy is emitted as STAT_CHANGED from variables.js
            if (name === 'max_energy') {
                this.values.maxEnergy = newVal;
                this._updateEnergyBar();
                return;
            }
            const oldVal = this.values[name] !== undefined ? this.values[name] : newVal;
            this._onStatChanged(name, newVal, oldVal);
        });

        // Listen for variable changes (Day, TimeSlot, HRT mood, compound buffs)
        this._scope.on(Events.VARIABLE_CHANGED, (name, value) => {
            if (this._blockStatUpdates && (name === 'Day' || name === 'TimeSlot')) return;
            if (name === 'Day') this._updateDay(value);
            if (name === 'TimeSlot') this._updateTimeSlot(value);
            if (name === 'hrt_mood_swing') this._updateHrtMoodIcons(value);
            // Compound buff check (any constituent flag change triggers re-eval)
            if (name === 'wearing_chastity' || name === 'EquippedFeminine') {
                this._updateCompoundBuffs();
            }
            // Money sub-indicators: allowance cap + rent reminder
            if (name === 'allowance_active' || name === 'RentAmount' ||
                name === 'RentDueDay' || name === 'moved_out' || name === 'Day') {
                this._updateMoneyExtra();
            }
        });

        // Also check compound buffs on flag changes
        this._scope.on(Events.FLAG_CHANGED, (flag, value) => {
            this._onFlagChanged(flag, value);
            if (flag === 'wearing_chastity' || flag === 'EquippedFeminine') {
                this._updateCompoundBuffs();
            }
            if (flag === 'allowance_active' || flag === 'moved_out') {
                this._updateMoneyExtra();
            }
        });

        // Initial render
        this._renderAll();

        // Add tier markers to presentation bar
        this._addTierMarkers();

        // Bind tooltips on stat rows and status icons
        this._bindTooltips();
    }

    // ------------------------------------------------------------------
    // Public API
    // ------------------------------------------------------------------

    /**
     * Set all stat values at once (e.g. on game load).
     * @param {object} stats - { Energy, Arousal, Femininity, Submissiveness, Money, Shame }
     */
    setAll(stats) {
        for (const [key, val] of Object.entries(stats)) {
            if (this.values[key] !== undefined) {
                this.values[key] = val;
            }
        }
        this._renderAll();
    }

    /**
     * Show a stat change notification (batched — accumulates over 300ms).
     * @param {string} statName
     * @param {number} delta
     */
    showNotification(statName, delta) {
        if (!this._notifContainer || delta === 0) return;

        // Accumulate deltas over a short window
        if (!this._pendingDeltas) this._pendingDeltas = {};
        this._pendingDeltas[statName] = (this._pendingDeltas[statName] || 0) + delta;

        if (!this._notifFlushTimer) {
            this._notifFlushTimer = setTimeout(() => {
                this._flushNotifications();
                this._notifFlushTimer = null;
            }, 300);
        }
    }

    _flushNotifications() {
        if (!this._pendingDeltas) return;
        const entries = Object.entries(this._pendingDeltas).filter(([, d]) => d !== 0);
        this._pendingDeltas = {};
        if (entries.length === 0) return;

        // Resolve context: prefer speaker, fall back to location
        let ctx = this._statContext || '';
        if (!ctx && storyManager.currentLocation) {
            // Use location as fallback context (strip "Entry" suffix)
            const loc = storyManager.currentLocation.replace(/Entry$/, '');
            ctx = loc.charAt(0).toUpperCase() + loc.slice(1);
        }
        this._statContext = ''; // Reset for next batch

        // Show-Don't-Tell: pick one flavor text for the most significant stat change
        const mainStat = entries.reduce((a, b) => Math.abs(b[1]) > Math.abs(a[1]) ? b : a);
        const flavor = STAT_FLAVOR[mainStat[0]];
        if (flavor && Math.abs(mainStat[1]) >= 2) {
            const pool = mainStat[1] > 0 ? flavor.pos : flavor.neg;
            const text = pool[Math.floor(Math.random() * pool.length)];
            const flavorEl = document.createElement('div');
            flavorEl.className = 'stat-flavor-text';
            flavorEl.textContent = text;
            this._notifContainer.appendChild(flavorEl);
            setTimeout(() => {
                flavorEl.classList.add('fade-out');
                setTimeout(() => flavorEl.remove(), 600);
            }, 2500);
        }

        // Sequential stagger: each notification 300ms apart, max 4 visible
        let staggerDelay = 200; // brief overlap with flavor text for wave effect
        entries.forEach(([statName, delta], i) => {
            setTimeout(() => {
                const sign = delta > 0 ? '+' : '';
                const displayName = this._getDisplayName(statName);
                const cssClass = STAT_CSS_MAP[statName] || '';

                const el = document.createElement('div');
                el.className = `stat-notification ${cssClass}`;
                const ctxSuffix = ctx ? ` · ${ctx}` : '';
                el.textContent = `${sign}${delta} ${displayName}${ctxSuffix}`;

                // Max 4 visible — remove oldest if exceeded
                const existing = this._notifContainer.querySelectorAll('.stat-notification:not(.fade-out)');
                if (existing.length >= 4 && existing[0]) {
                    existing[0].classList.add('fade-out');
                    setTimeout(() => existing[0].remove(), 400);
                }

                this._notifContainer.appendChild(el);

                setTimeout(() => {
                    el.classList.add('fade-out');
                    setTimeout(() => el.remove(), 500);
                }, 2000);

                // Inline stat popup near story area
                this._showInlineStatPopup(statName, delta, 0);
            }, staggerDelay);
            staggerDelay += 300;
        });
    }

    /**
     * Show a generic toast notification.
     * @param {string} message
     * @param {string} cssClass
     */
    showToast(message, cssClass = '') {
        if (!this._notifContainer) return;

        const el = document.createElement('div');
        el.className = `stat-notification ${cssClass}`;
        el.textContent = message;
        this._notifContainer.appendChild(el);

        setTimeout(() => {
            el.classList.add('fade-out');
            setTimeout(() => el.remove(), 500);
        }, 2000);
    }

    // ------------------------------------------------------------------
    // Private: Element Cache
    // ------------------------------------------------------------------

    _cacheElements() {
        const ids = [
            // Mini bars
            'bar-energy', 'bar-arousal', 'bar-masc', 'bar-fem', 'bar-fem-pink', 'bar-sub',
            'num-energy', 'num-arousal', 'num-fem', 'num-sub', 'num-money',
            // Full bars
            'full-bar-energy', 'full-bar-arousal', 'full-bar-masc', 'full-bar-fem', 'full-bar-fem-pink', 'full-bar-sub',
            'full-bar-shame',
            'full-num-energy', 'full-num-arousal', 'full-num-masc', 'full-num-fem', 'full-num-sub',
            'full-num-shame', 'full-num-money',
            // Money sub-indicators (allowance + rent, Owner route)
            'money-extra', 'money-allowance', 'money-rent',
            // Time
            'day-display',
        ];

        for (const id of ids) {
            this._elements[id] = document.getElementById(id);
        }

        // Time segments
        this._elements.timeSegs = document.querySelectorAll('.time-seg');

        // Status icons
        for (const [flag, elemId] of Object.entries(STATUS_FLAG_MAP)) {
            this._elements[`status-${flag}`] = document.getElementById(elemId);
        }

        // HRT mood icons
        for (const [val, elemId] of Object.entries(HRT_MOOD_ICONS)) {
            this._elements[`hrt-${val}`] = document.getElementById(elemId);
        }

        // Compound buff icons
        for (const elemId of Object.keys(COMPOUND_BUFF_FLAGS)) {
            this._elements[`compound-${elemId}`] = document.getElementById(elemId);
        }
    }

    // ------------------------------------------------------------------
    // Private: Stat Change Handler
    // ------------------------------------------------------------------

    _onStatChanged(name, newVal, oldVal) {
        const delta = newVal - oldVal;
        this.values[name] = newVal;

        // Update bars
        switch (name) {
            case Stats.ENERGY:
                this._updateEnergyBar();
                break;
            case Stats.AROUSAL:
                this._updateArousalBar();
                break;
            case Stats.FEMININITY:
                this._updatePresentationBar();
                break;
            case Stats.SUBMISSIVENESS:
                this._updateSubBar();
                break;
            case Stats.MONEY:
                this._updateMoney(oldVal, newVal);
                break;
            case Stats.SHAME:
                this._updateShameBar();
                break;
        }

        // Show notification (skip TimeSlot/Day)
        // During story processing: queue deltas, flush after STORY_CHOICES_READY
        if (Math.abs(delta) >= 1 && name !== 'TimeSlot' && name !== 'Day') {
            if (this._suppressNotifications) {
                this._pendingDeltas[name] = (this._pendingDeltas[name] || 0) + delta;
            } else {
                this.showNotification(name, delta);
            }
        }

        // Inline delta on stat bar
        if (name !== Stats.MONEY && Math.abs(delta) >= 1) {
            this._showInlineDelta(name, delta);
            this._triggerGlow(name, delta);
        }

        // Money delta animation — floats `+$X` / `-$X` off the money indicator
        if (name === Stats.MONEY && Math.abs(delta) >= 1) {
            this._showMoneyDelta(delta);
        }

        // Fem tier shimmer + particles + centered toast (crossing 25/50/75)
        // U-06 v0.2.5: shimmer was alone before; tier-up now also spawns a
        // small local particle burst from the bar AND a centered tier-name
        // reveal toast. Per user spec: no SFX, particles small/local only.
        if (name === 'Femininity' && delta > 0) {
            const TIERS = [
                { threshold: 25, name: L.get('fem.tier1') || 'Exploring' },
                { threshold: 50, name: L.get('fem.tier2') || 'Embracing' },
                { threshold: 75, name: L.get('fem.tier3') || 'Transformed' },
            ];
            for (const t of TIERS) {
                if (oldVal < t.threshold && newVal >= t.threshold) {
                    const row = this._elements['full-bar-fem']?.closest('.stat-row');
                    if (row) {
                        row.classList.add('fem-tier-shimmer');
                        setTimeout(() => row.classList.remove('fem-tier-shimmer'), 1200);
                        this._spawnTierParticles(row);
                    }
                    this._showTierUpToast(t.name);
                    break;
                }
            }
        }

        // Shame blush (crossing 50/75/90)
        if (name === 'Shame' && delta > 0) {
            const THRESHOLDS = [50, 75, 90];
            for (const t of THRESHOLDS) {
                if (oldVal < t && newVal >= t) {
                    const charPanel = document.getElementById('right-column');
                    if (charPanel) {
                        charPanel.classList.add('shame-blush');
                        setTimeout(() => charPanel.classList.remove('shame-blush'), 1500);
                    }
                    break;
                }
            }
        }
    }

    // U-06 v0.2.5: Tier-up FX. Small local particles + centered toast.
    _spawnTierParticles(row) {
        try {
            const rect = row.getBoundingClientRect();
            const layer = document.createElement('div');
            layer.className = 'tier-particles';
            layer.style.left = `${rect.left}px`;
            layer.style.top = `${rect.top}px`;
            layer.style.width = `${rect.width}px`;
            layer.style.height = `${rect.height}px`;
            for (let i = 0; i < 12; i++) {
                const p = document.createElement('span');
                p.className = 'tier-particle';
                const angle = (Math.PI * 2 * i) / 12 + (Math.random() * 0.4 - 0.2);
                const dist = 50 + Math.random() * 25;
                p.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
                p.style.setProperty('--dy', `${Math.sin(angle) * dist - 18}px`);
                p.style.setProperty('--delay', `${i * 25}ms`);
                layer.appendChild(p);
            }
            document.body.appendChild(layer);
            setTimeout(() => layer.remove(), 1400);
        } catch (e) { console.warn('[Stats] tier-particles failed:', e?.message); }
    }

    _showTierUpToast(tierName) {
        try {
            const toast = document.createElement('div');
            toast.className = 'tier-up-toast';
            const labelText = L.get('tier_up.label') || 'TIER UP';
            const safeName = String(tierName).replace(/[<>&]/g, '');
            toast.innerHTML = `<div class="tier-up-label">${labelText}</div><div class="tier-up-name">${safeName}</div>`;
            document.body.appendChild(toast);
            requestAnimationFrame(() => toast.classList.add('show'));
            setTimeout(() => {
                toast.classList.remove('show');
                toast.classList.add('hide');
                setTimeout(() => toast.remove(), 600);
            }, 2200);
        } catch (e) { console.warn('[Stats] tier-up toast failed:', e?.message); }
    }

    _onFlagChanged(flag, value) {
        const elemId = STATUS_FLAG_MAP[flag];
        const el = elemId ? (this._elements[elemId] || document.getElementById(elemId)) : null;
        if (el) {
            el.classList.toggle('hidden', !value);
        }
    }

    // ------------------------------------------------------------------
    // Private: Bar Updates
    // ------------------------------------------------------------------

    _syncFromInk() {
        // Read all stat values directly from Ink story and update bars
        try {
            const story = window._sm?.currentStory;
            if (!story) return;
            const stats = {};
            for (const key of ['Energy', 'Arousal', 'Femininity', 'Submissiveness', 'Money', 'Shame']) {
                stats[key] = story.variablesState[key] || 0;
            }
            stats.Day = story.variablesState['Day'] || 1;
            stats.TimeSlot = story.variablesState['TimeSlot'] || 0;
            stats.maxEnergy = story.variablesState['max_energy'] || 100;
            this.setAll(stats);

            // Sync status flags (icons) from Ink — observer may not fire if value didn't change
            for (const [flag, elemId] of Object.entries(STATUS_FLAG_MAP)) {
                const val = story.variablesState[flag];
                const el = this._elements[elemId] || document.getElementById(elemId);
                if (el) el.classList.toggle('hidden', !val);
            }

            // Refresh money sub-indicators (allowance + rent state may have shifted overnight)
            this._updateMoneyExtra();
        } catch(e) {
            console.warn('[StatsUI] _syncFromInk failed:', e);
        }
    }

    _renderAll() {
        this._updateEnergyBar();
        this._updateArousalBar();
        this._updatePresentationBar();
        this._updateSubBar();
        this._updateShameBar();
        this._updateMoneyDisplay(this.values.Money);
        this._updateMoneyExtra();
        this._updateDay(this.values.Day);
        this._updateTimeSlot(this.values.TimeSlot);
    }

    _updateEnergyBar() {
        const val = this.values.Energy;
        const max = this.values.maxEnergy || 100;
        const pct = Math.min(100, (val / max) * 100);

        this._setBar('bar-energy', pct);
        this._setBar('full-bar-energy', pct);
        this._setText('num-energy', val);
        this._setText('full-num-energy', val);
        this._setText('topbar-energy', val);

        // Color change based on level
        const energyClass = val <= 15 ? 'critical' : val <= 30 ? 'low' : '';
        ['bar-energy', 'full-bar-energy'].forEach(id => {
            const el = this._elements[id];
            if (el) {
                el.classList.remove('low', 'critical');
                if (energyClass) el.classList.add(energyClass);
            }
        });

        const energyVal = this._elements['topbar-energy'];
        if (energyVal) {
            energyVal.classList.remove('low', 'critical');
            if (energyClass) energyVal.classList.add(energyClass);
        }
    }

    _updateArousalBar() {
        const val = Math.max(0, Math.min(100, this.values.Arousal));
        this._setBar('bar-arousal', val);
        this._setBar('full-bar-arousal', val);
        this._setText('num-arousal', val);
        this._setText('full-num-arousal', val);
    }

    _updatePresentationBar() {
        // Transformation Ladder: Femininity can reach 200 once fully_transformed
        // (pink deepening zone). 0-100 = GREEN "becoming" fill with the masc↔fem
        // split; 100-200 = PINK "deepening" that fills the SAME track a second
        // time ("Bar füllt sich zweimal"). Owner design 2026-07-01.
        const femRaw = Math.max(0, Math.min(200, this.values.Femininity));
        const fem = Math.min(100, femRaw);         // masc↔fem split value (0-100)
        const masc = 100 - fem;

        // Pacing 2.0: fem_accum is the banked sub-1 progress toward the next whole
        // point (Ink-only, not observed) — add it to the ACTIVE stage's fill so
        // the bar visibly creeps between whole points. Number stays integer.
        let accum = 0;
        try {
            const a = storyManager?.currentStory?.variablesState?.['fem_accum'];
            if (typeof a === 'number' && a > 0) accum = Math.min(0.999, a);
        } catch (e) { /* ignore */ }

        let greenFill, pinkFill;
        if (femRaw <= 100) {
            greenFill = Math.min(100, fem + (fem < 100 ? accum : 0));
            pinkFill = 0;
        } else {
            greenFill = 100;                       // green stage complete
            pinkFill = Math.min(100, (femRaw - 100) + accum);
        }

        // Mini bars
        this._setBar('bar-masc', masc);
        this._setBar('bar-fem', greenFill);
        this._setBar('bar-fem-pink', pinkFill);
        this._setText('num-fem', femRaw);

        // Full bars
        this._setBar('full-bar-masc', masc);
        this._setBar('full-bar-fem', greenFill);
        this._setBar('full-bar-fem-pink', pinkFill);
        this._setText('full-num-masc', masc);
        this._setText('full-num-fem', femRaw);

        // Resolve tier name + next-threshold delta once for both labels.
        // Pink deepening zone (Fem 100-200, post-capstone) gets its own label.
        const inPink = femRaw > 100;
        const tier = inPink ? (L.get('fem.tier_pink') || 'Deepening')
            : fem >= 75 ? (L.get('fem.tier3') || 'Transformed')
            : fem >= 50 ? (L.get('fem.tier2') || 'Embracing')
            : fem >= 25 ? (L.get('fem.tier1') || 'Exploring')
            : (L.get('fem.tier0') || 'Curious');
        const nextAt = inPink ? null : fem >= 75 ? null : fem >= 50 ? 75 : fem >= 25 ? 50 : 25;
        const nextTier = (inPink || fem >= 75) ? null : fem >= 50 ? (L.get('fem.tier3') || 'Transformed')
            : fem >= 25 ? (L.get('fem.tier2') || 'Embracing')
            : (L.get('fem.tier1') || 'Exploring');

        // Fem tier label (full-stats panel)
        const tierLabel = document.getElementById('fem-tier-label');
        if (tierLabel) tierLabel.textContent = tier;

        // UX-09: mini-statbar tooltip + data-attribute for tier-pill CSS rendering.
        // Players running with collapsed left panel lose Fem-tier context;
        // hovering num-fem now reveals "Fem 32 (Exploring) — +13 to Embracing".
        const numFem = this._elements['num-fem'];
        if (numFem) {
            numFem.dataset.tier = tier;
            const delta = (nextAt !== null) ? (nextAt - fem) : 0;
            numFem.title = nextAt
                ? `Femininity ${fem} (${tier}) — +${delta} to ${nextTier}`
                : `Femininity ${fem} (${tier}) — max tier`;
        }
    }

    _addTierMarkers() {
        const track = this._elements['full-bar-fem']?.closest('.presentation-track');
        if (!track) return;
        for (const pct of [25, 50, 75]) {
            const marker = document.createElement('div');
            marker.className = 'tier-marker';
            marker.style.left = pct + '%';
            track.appendChild(marker);
        }
    }

    _updateSubBar() {
        const val = Math.max(0, Math.min(100, this.values.Submissiveness));
        this._setBar('bar-sub', val);
        this._setBar('full-bar-sub', val);
        this._setText('num-sub', val);
        this._setText('full-num-sub', val);
    }

    _updateShameBar() {
        const val = Math.max(0, Math.min(100, this.values.Shame));
        this._setBar('full-bar-shame', val);
        this._setText('full-num-shame', val);
    }

    _updateMoney(oldVal, newVal) {
        // Animated tick
        const moneyEl = this._elements['full-num-money'];
        if (moneyEl) {
            const isUp = newVal > oldVal;
            moneyEl.classList.add(isUp ? 'tick-up' : 'tick-down');
            setTimeout(() => moneyEl.classList.remove('tick-up', 'tick-down'), 600);
        }

        // Animated counter
        if (this._moneyTickTimer) clearInterval(this._moneyTickTimer);

        const duration = 400; // ms
        const steps = 10;
        const stepTime = duration / steps;
        const diff = newVal - oldVal;
        let current = oldVal;
        let step = 0;

        this._moneyTickTimer = setInterval(() => {
            step++;
            if (step >= steps) {
                current = newVal;
                clearInterval(this._moneyTickTimer);
                this._moneyTickTimer = null;
            } else {
                current = Math.round(oldVal + (diff * (step / steps)));
            }
            this._updateMoneyDisplay(current);
        }, stepTime);
    }

    _updateMoneyDisplay(val) {
        this._setText('full-num-money', `$${val}`);
        this._setText('num-money', `$${val}`);
        this._setText('topbar-money', val);
    }

    /**
     * Render Owner-route money sub-indicators: daily allowance cap + weekly rent.
     * Reads Ink VARs directly (RentAmount, RentDueDay, allowance_active, moved_out
     * are not in OBSERVED_VARIABLES, so variableManager doesn't track them).
     * Hides itself entirely when no relevant info applies (moved out + no allowance).
     */
    _updateMoneyExtra() {
        const wrap = this._elements['money-extra'];
        const allowanceEl = this._elements['money-allowance'];
        const rentEl = this._elements['money-rent'];
        if (!wrap || !allowanceEl || !rentEl) return;

        const story = storyManager?.currentStory;
        const iv = (v) => { try { return story?.variablesState[v]; } catch(e) { return undefined; } };
        const movedOut = iv('moved_out') === true || iv('moved_out') === 1;
        const allowanceActive = iv('allowance_active') === true || iv('allowance_active') === 1;
        const rentAmount = Number(iv('RentAmount')) || 0;
        const rentDueDay = Number(iv('RentDueDay')) || 0;
        const day = Number(iv('Day')) || this.values.Day || 0;

        // Allowance line: only when active and still in the apartment
        if (allowanceActive && !movedOut) {
            const tpl = L.get('money.allowance_line') || 'Allowance: $350/day';
            allowanceEl.textContent = tpl;
            allowanceEl.classList.remove('hidden');
        } else {
            allowanceEl.classList.add('hidden');
        }

        // Rent line: only while living with Master
        if (!movedOut && rentAmount > 0 && rentDueDay > 0) {
            const daysUntil = rentDueDay - day;
            let tpl;
            if (daysUntil <= 0) {
                tpl = L.get('money.rent_due') || 'Rent: ${0} due today';
            } else if (daysUntil === 1) {
                tpl = L.get('money.rent_tomorrow') || 'Rent: ${0} due tomorrow';
            } else {
                tpl = L.get('money.rent_line') || 'Rent: ${0} (Day {1})';
            }
            rentEl.textContent = tpl.replace('{0}', rentAmount).replace('{1}', rentDueDay);
            rentEl.classList.toggle('due-soon', daysUntil <= 1);
            rentEl.classList.remove('hidden');
        } else {
            rentEl.classList.add('hidden');
            rentEl.classList.remove('due-soon');
        }

        // Toggle wrapper visibility
        const anyVisible = !allowanceEl.classList.contains('hidden') ||
                           !rentEl.classList.contains('hidden');
        wrap.classList.toggle('hidden', !anyVisible);
    }

    _updateDay(day) {
        const oldDay = this.values.Day;
        this.values.Day = day;
        const el = this._elements['day-display'];
        if (el) {
            const dayKeys = ['day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat', 'day.sun'];
            const weekday = L.get(dayKeys[(day - 1) % 7]) || ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][(day - 1) % 7];
            const dayLabel = L.get('desktop.day') || 'Day {0}';
            const _ts = (k, fb) => { const v = L.get(k); return v !== k ? v : fb; };
            const act = day <= 7 ? _ts('stats.act.early', 'New Beginnings')
                : day <= 14 ? _ts('stats.act.midgame', 'Double Life')
                : day <= 21 ? _ts('stats.act.lockin', 'Point of No Return')
                : day <= 30 ? _ts('stats.act.endgame', 'Transformation')
                : _ts('stats.act.postgame', 'New Normal');
            const actHint = day <= 7
                ? _ts('stats.act.early_hint', 'Early days. Master intensity and Girlfriend settings still fluid.')
                : day <= 14
                    ? _ts('stats.act.midgame_hint', 'Relationships firm up. Choose your path: Master, Sarah, or solo.')
                    : day <= 21
                        ? _ts('stats.act.lockin_hint', 'Character lock-in. Past this block, your arc commits to its ending.')
                        : day <= 30
                            ? _ts('stats.act.endgame_hint', 'Endgame arc. Body, relationships and identity settle in.')
                            : _ts('stats.act.postgame_hint', 'Post-story content. Free play in your chosen life.');
            el.innerHTML = `${dayLabel.replace('{0}', day)}<span class="day-weekday">${weekday}</span><span class="day-act" title="${actHint}">${act}</span>`;
        }
        this._renderWeekStrip(day);

        // Day transition overlay (skip initial render / same day)
        if (oldDay > 0 && day > oldDay) {
            this._showDayTransition(day);
        }
    }

    _renderWeekStrip(day) {
        const stripEl = document.getElementById('week-strip');
        const popInnerEl = document.getElementById('week-strip-popover-inner');
        if (!stripEl && !popInnerEl) return;

        const story = storyManager?.currentStory;
        const vs = story ? story.variablesState : null;
        const rentDueDay = vs ? (vs['RentDueDay'] || 0) : 0;
        const gfDisabled = vs ? !!vs['girlfriend_disabled'] : false;
        const gfCucked = vs ? !!vs['girlfriend_cucked'] : false;
        const gfBrokenUp = vs ? !!vs['girlfriend_broken_up'] : false;
        const weekendPending = vs ? (vs['weekend_event_pending'] || 0) : 0;
        const gfActive = !gfDisabled && !gfCucked && !gfBrokenUp;
        const showWeekendDot = gfActive || weekendPending > 0;

        const wdKeys = ['day.mon_short', 'day.tue_short', 'day.wed_short', 'day.thu_short', 'day.fri_short', 'day.sat_short', 'day.sun_short'];
        const wdFB = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
        const wdFullKeys = ['day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat', 'day.sun'];
        const wdFullFB = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
        const _ts = (k, fb) => { const v = L.get(k); return v !== k ? v : fb; };
        const rentTitle = _ts('week.rent_due', 'Rent due');
        const weekendTitle = _ts('week.weekend_event', 'Weekend plans');

        // Forward-looking window: today + the next 6 days. The old Mon-Sun
        // week rendered up to 6 .past cells which only cluttered the strip/
        // popover (player ask 0.2.7.6.1: "I don't need to see the previous
        // days"). Weekday position is derived from the DAY (Day 1 = Monday),
        // not the loop index — the window no longer starts on Monday.
        const cells = [];
        for (let i = 0; i < 7; i++) {
            const cellDay = day + i;
            const wd = (cellDay - 1) % 7;
            const wdLabel = _ts(wdKeys[wd], wdFB[wd]);
            const wdFullLabel = _ts(wdFullKeys[wd], wdFullFB[wd]);
            const isCurrent = (cellDay === day);
            const isRentDay = (rentDueDay > 0 && cellDay === rentDueDay);
            const isSaturday = (wd === 5);
            const showWeekend = isSaturday && showWeekendDot;

            const classes = ['week-strip-cell'];
            if (isCurrent) classes.push('current');

            const markers = [];
            if (isRentDay) markers.push(`<span class="ws-dot ws-dot-rent" title="${rentTitle}"></span>`);
            if (showWeekend) markers.push(`<span class="ws-dot ws-dot-weekend" title="${weekendTitle}"></span>`);

            const titleBits = [`${wdFullLabel} — Day ${cellDay}`];
            if (isRentDay) titleBits.push(rentTitle);
            if (showWeekend) titleBits.push(weekendTitle);
            const title = titleBits.join(' · ');

            cells.push(
                `<div class="${classes.join(' ')}" data-day="${cellDay}" title="${title}" role="listitem">` +
                `<div class="ws-wd">${wdLabel}</div>` +
                `<div class="ws-num">${cellDay}</div>` +
                (markers.length ? `<div class="ws-markers">${markers.join('')}</div>` : '') +
                `</div>`
            );
        }

        const cellsHtml = cells.join('');
        if (stripEl) stripEl.innerHTML = cellsHtml;
        if (popInnerEl) {
            const legendRent = _ts('week.legend_rent', 'Rent');
            const legendWeekend = _ts('week.legend_weekend', 'Weekend');
            popInnerEl.innerHTML = cellsHtml;
            const popEl = document.getElementById('week-strip-popover');
            if (popEl && !popEl.dataset.legendInjected) {
                const legend = document.createElement('div');
                legend.className = 'week-strip-popover-legend';
                legend.innerHTML =
                    `<span><span class="lg-dot" style="background:#ffcb5c"></span>${legendRent}</span>` +
                    `<span><span class="lg-dot" style="background:#f56aa3"></span>${legendWeekend}</span>`;
                popEl.appendChild(legend);
                popEl.dataset.legendInjected = '1';
            }
        }
    }

    _initWeekStripPopover() {
        const btn = document.getElementById('day-display');
        const pop = document.getElementById('week-strip-popover');
        if (!btn || !pop || btn.dataset.popoverBound) return;
        btn.dataset.popoverBound = '1';

        const isMobile = () => window.matchMedia('(max-width: 767px)').matches;
        const close = () => {
            pop.classList.remove('open');
            pop.setAttribute('aria-hidden', 'true');
            btn.setAttribute('aria-expanded', 'false');
        };
        const open = () => {
            pop.classList.add('open');
            pop.setAttribute('aria-hidden', 'false');
            btn.setAttribute('aria-expanded', 'true');
        };

        btn.addEventListener('click', (ev) => {
            if (!isMobile()) return;
            ev.stopPropagation();
            if (pop.classList.contains('open')) close(); else open();
        });
        this._popoverDocClick = (ev) => {
            if (!pop.classList.contains('open')) return;
            if (ev.target === btn || btn.contains(ev.target)) return;
            if (pop.contains(ev.target)) return;
            close();
        };
        document.addEventListener('click', this._popoverDocClick);
        this._popoverKeydown = (ev) => {
            if (ev.key === 'Escape' && pop.classList.contains('open')) close();
        };
        document.addEventListener('keydown', this._popoverKeydown);
        this._popoverResizeClose = () => {
            if (!isMobile() && pop.classList.contains('open')) close();
        };
        window.addEventListener('resize', this._popoverResizeClose);
        // Android WebView visualViewport sibling (BUG_PATTERNS § 76).
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this._popoverResizeClose);
        }
    }

    _updateTimeSlot(slot) {
        this.values.TimeSlot = slot;
        const segs = this._elements.timeSegs;
        if (!segs) return;

        segs.forEach((seg, i) => {
            seg.classList.remove('active', 'past');
            if (i < slot) seg.classList.add('past');
            else if (i === slot) seg.classList.add('active');
        });

        // Apply time-of-day class to game view root (drives topbar/panel color transitions)
        const gameView = document.getElementById('screen-game-view');
        if (gameView) {
            const TIME_CLASSES = ['time-morning', 'time-lunch', 'time-evening', 'time-night'];
            TIME_CLASSES.forEach(c => gameView.classList.remove(c));
            const idx = Math.max(0, Math.min(3, slot));
            gameView.classList.add(TIME_CLASSES[idx]);
        }

        // Apply ambient color overlay to body
        const BODY_TIME_CLASSES = ['time-morning', 'time-afternoon', 'time-evening', 'time-night'];
        BODY_TIME_CLASSES.forEach(c => document.body.classList.remove(c));
        const bodyIdx = Math.max(0, Math.min(3, slot));
        document.body.classList.add(BODY_TIME_CLASSES[bodyIdx]);
    }

    // ------------------------------------------------------------------
    // Private: Glow Pulse
    // ------------------------------------------------------------------

    _triggerGlow(statName, delta) {
        const barId = this._getBarIdForStat(statName);
        const barEl = this._elements[barId];
        if (!barEl) return;

        const row = barEl.closest('.stat-row');
        if (!row) return;

        // Arousal gets pink glow, others green/red
        let glowClass;
        if (statName === 'Arousal' && delta > 0) glowClass = 'stat-glow-arousal';
        else glowClass = delta > 0 ? 'stat-glow-positive' : 'stat-glow-negative';
        row.classList.remove('stat-glow-positive', 'stat-glow-negative', 'stat-glow-arousal');

        // Re-add next frame so CSS animation resets
        requestAnimationFrame(() => {
            row.classList.add(glowClass);
            setTimeout(() => row.classList.remove(glowClass), 600);
        });
    }

    // ------------------------------------------------------------------
    // Private: Inline Delta
    // ------------------------------------------------------------------

    /**
     * Float a `+$X` / `-$X` delta off the money value. Separate from the
     * stat-bar inline delta because the money row has no bar element.
     */
    _showMoneyDelta(delta) {
        const moneyVal = this._elements['full-num-money']
            || document.getElementById('full-num-money');
        if (!moneyVal) return;
        const row = moneyVal.closest('.stat-row') || moneyVal.parentElement;
        if (!row) return;

        const sign = delta > 0 ? '+' : '−';
        const abs = Math.abs(delta);
        const el = document.createElement('span');
        const signClass = delta > 0 ? 'stat-delta-inline-positive' : 'stat-delta-inline-negative';
        el.className = `stat-delta-inline ${signClass} delta-money`;
        el.textContent = `${sign}$${abs}`;
        // Position anchor on the money number so the float starts visually
        // close to the $XXX value, not at the row origin.
        row.style.position = 'relative';
        el.style.right = '4px';
        el.style.top = '2px';
        el.style.left = 'auto';
        row.appendChild(el);

        if (typeof gsap !== 'undefined') {
            gsap.fromTo(el,
                { y: 0, opacity: 0, scale: 0.6 },
                { y: -4, opacity: 1, scale: 1.15, duration: 0.22, ease: 'back.out(2.2)' }
            );
            gsap.to(el,
                { y: -26, scale: 1, opacity: 0, duration: 1.3, ease: 'power2.out', delay: 0.35, onComplete: () => el.remove() }
            );
        } else {
            setTimeout(() => el.remove(), 1500);
        }
    }

    _showInlineDelta(statName, delta) {
        // Find the stat row
        const barId = this._getBarIdForStat(statName);
        const barEl = this._elements[barId];
        if (!barEl) return;

        const row = barEl.closest('.stat-row');
        if (!row) return;

        // Stat-specific color class for readable, game-coherent float numbers
        const DELTA_STAT_CLASS = {
            [Stats.FEMININITY]: 'delta-fem',
            [Stats.SUBMISSIVENESS]: 'delta-sub',
            [Stats.AROUSAL]: 'delta-arousal',
            [Stats.ENERGY]: 'delta-energy',
            [Stats.SHAME]: 'delta-shame',
            [Stats.MONEY]: 'delta-money',
        };
        const statClass = DELTA_STAT_CLASS[statName] || '';
        const signClass = delta > 0 ? 'stat-delta-inline-positive' : 'stat-delta-inline-negative';

        const el = document.createElement('span');
        el.className = `stat-delta-inline ${signClass} ${statClass}`;
        el.textContent = `${delta > 0 ? '+' : ''}${delta}`;
        row.style.position = 'relative';
        row.appendChild(el);

        if (typeof gsap !== 'undefined') {
            // Two-phase: quick scale-up punch (0.2s), then slow float-up + fade
            gsap.fromTo(el,
                { y: 0, opacity: 0, scale: 0.6 },
                { y: -4, opacity: 1, scale: 1.15, duration: 0.22, ease: 'back.out(2.2)' }
            );
            gsap.to(el,
                { y: -28, scale: 1, opacity: 0, duration: 1.3, ease: 'power2.out', delay: 0.35, onComplete: () => el.remove() }
            );
        } else {
            setTimeout(() => el.remove(), 1500);
        }
    }

    // ------------------------------------------------------------------
    // HRT Mood & Compound Buffs
    // ------------------------------------------------------------------

    _updateHrtMoodIcons(value) {
        for (const [moodVal, elemId] of Object.entries(HRT_MOOD_ICONS)) {
            const el = this._elements[`hrt-${moodVal}`];
            if (el) el.classList.toggle('hidden', parseInt(moodVal) !== value);
        }
    }

    _updateCompoundBuffs() {
        for (const [elemId, flags] of Object.entries(COMPOUND_BUFF_FLAGS)) {
            const el = this._elements[`compound-${elemId}`];
            if (!el) continue;
            // All constituent flags must be truthy
            const allTrue = flags.every(flag => {
                const val = this._getVarValue?.(flag);
                return val === true || val === 1;
            });
            el.classList.toggle('hidden', !allTrue);
        }
    }

    /** Set a variable accessor so compound buffs can query current state */
    setVarAccessor(fn) {
        this._getVarValue = fn;
    }

    // ------------------------------------------------------------------
    // Tooltips
    // ------------------------------------------------------------------

    /**
     * Get the tier info string for a stat that uses diminishing returns.
     * @param {number} baseVal - base stat value (before buffs)
     * @returns {{ tier: number, name: string, multiplier: string }}
     */
    _getTierInfo(baseVal) {
        // Multipliers MUST match FEM_TIER_MULTIPLIERS / SUB_TIER_MULTIPLIERS in externals.js
        if (baseVal >= 75) return { tier: 3, name: L.get('fem.tier3') || 'Transformed', multiplier: '0.25' };
        if (baseVal >= 50) return { tier: 2, name: L.get('fem.tier2') || 'Embracing', multiplier: '0.5' };
        if (baseVal >= 25) return { tier: 1, name: L.get('fem.tier1') || 'Exploring', multiplier: '0.6' };
        return { tier: 0, name: L.get('fem.tier0') || 'Curious', multiplier: '0.85' };
    }

    _bindTooltips() {
        // Stat row tooltips (keyed by data-stat attribute)
        const STAT_TOOLTIP_KEYS = {
            energy: 'tooltip.stat.energy',
            arousal: null, // dynamic tooltip below
            presentation: 'tooltip.stat.presentation',
            submissiveness: 'tooltip.stat.submissiveness',
            shame: 'tooltip.stat.shame',
        };

        document.querySelectorAll('.stat-row[data-stat]').forEach(row => {
            const stat = row.dataset.stat;

            // --- Arousal: dynamic value tooltip ---
            if (stat === 'arousal') {
                this._addTooltip(row, () => {
                    const val = Math.max(0, Math.min(100, this.values.Arousal));
                    const base = L.get('tooltip.stat.arousal');
                    const desc = (base && !base.startsWith('tooltip.'))
                        ? base
                        : 'Sexual arousal. Above 30 visibly aroused (+8 Energy/action), above 50 inflates energy costs further. Triggers accidental orgasms. Drains via relief, cold shower, sleep.';
                    return `Arousal: ${val} / 100\n${desc}`;
                });
                return;
            }

            // --- Energy: dynamic value with max ---
            if (stat === 'energy') {
                this._addTooltip(row, () => {
                    const val = this.values.Energy;
                    const max = this.values.maxEnergy || 100;
                    const base = L.get(STAT_TOOLTIP_KEYS.energy);
                    const desc = (base && !base.startsWith('tooltip.')) ? base : 'Actions cost energy. Rest or advance time to recover.';
                    return `Energy: ${val} / ${max}\n${desc}`;
                });
                return;
            }

            const key = STAT_TOOLTIP_KEYS[stat];
            if (!key) return;

            // --- Presentation (Fem/Masc dual bar) with tier info ---
            if (stat === 'presentation') {
                this._addTooltip(row, () => {
                    const fem = Math.max(0, Math.min(100, this.values.Femininity));
                    const masc = 100 - fem;
                    const tierInfo = this._getTierInfo(fem);
                    const base = L.get(key);
                    const desc = (base && !base.startsWith('tooltip.')) ? base : '';
                    let text = `Presentation: Masc ${masc} / Fem ${fem} — Fem Tier ${tierInfo.tier} (${tierInfo.multiplier}\u00d7 gain)`;
                    // Show buff breakdown if available
                    try {
                        if (this._getVarValue) {
                            const baseVal = this._getVarValue('base_Femininity') || this.values.Femininity || 0;
                            const buffedVal = this._getVarValue('buffed_Femininity') || 0;
                            if (buffedVal > 0) {
                                text += `\nBase: ${baseVal} + Buffs: +${buffedVal}`;
                            }
                        }
                    } catch(e) {}
                    if (desc) text += `\n${desc}`;
                    return text;
                });
                return;
            }

            // --- Submissiveness with tier info ---
            if (stat === 'submissiveness') {
                this._addTooltip(row, () => {
                    const val = Math.max(0, Math.min(100, this.values.Submissiveness));
                    const tierInfo = this._getTierInfo(val);
                    const base = L.get(key);
                    const desc = (base && !base.startsWith('tooltip.')) ? base : '';
                    let text = `Submissiveness: ${val} / 100 — Tier ${tierInfo.tier} (${tierInfo.multiplier}\u00d7 gain rate)`;
                    try {
                        if (this._getVarValue) {
                            const baseVal = this._getVarValue('base_Submissiveness') || this.values.Submissiveness || 0;
                            const buffedVal = this._getVarValue('buffed_Submissiveness') || 0;
                            if (buffedVal > 0) {
                                text += `\nBase: ${baseVal} + Buffs: +${buffedVal}`;
                            }
                        }
                    } catch(e) {}
                    if (desc) text += `\n${desc}`;
                    return text;
                });
                return;
            }

            // --- Shame: dynamic value tooltip ---
            if (stat === 'shame') {
                this._addTooltip(row, () => {
                    const val = Math.max(0, Math.min(100, this.values.Shame));
                    const base = L.get(key);
                    const desc = (base && !base.startsWith('tooltip.')) ? base : 'Builds from public exposure. Decays overnight.';
                    let text = `Shame: ${val} / 100\n${desc}`;
                    try {
                        if (this._getVarValue) {
                            const baseVal = this._getVarValue('base_Shame') || this.values.Shame || 0;
                            const buffedVal = this._getVarValue('buffed_Shame') || 0;
                            if (buffedVal > 0) {
                                text += `\nBase: ${baseVal} + Buffs: +${buffedVal}`;
                            }
                        }
                    } catch(e) {}
                    return text;
                });
                return;
            }

            // --- Fallback: static tooltip ---
            this._addTooltip(row, () => L.get(key));
        });

        // Money row tooltip (no data-stat attribute)
        const moneyRow = document.querySelector('.money-row');
        if (moneyRow) {
            this._addTooltip(moneyRow, () => {
                const val = this.values.Money;
                const base = L.get('tooltip.money');
                const desc = (base && !base.startsWith('tooltip.')) ? base : 'Your current funds.';
                let text = `Money: $${val}\n${desc}`;

                // Append allowance + rent context for the Owner route
                try {
                    const story = storyManager?.currentStory;
                    const iv = (v) => { try { return story?.variablesState[v]; } catch(e) { return undefined; } };
                    const movedOut = iv('moved_out') === true || iv('moved_out') === 1;
                    const allowanceActive = iv('allowance_active') === true || iv('allowance_active') === 1;
                    const rentAmount = Number(iv('RentAmount')) || 0;
                    const rentDueDay = Number(iv('RentDueDay')) || 0;
                    const day = Number(iv('Day')) || this.values.Day || 0;

                    if (allowanceActive && !movedOut) {
                        const tip = L.get('tooltip.money.allowance')
                            || 'Allowance: capped at $350 each morning. Excess goes to {MasterName}.';
                        text += `\n\n${tip}`;
                    }
                    if (!movedOut && rentAmount > 0 && rentDueDay > 0) {
                        const daysUntil = rentDueDay - day;
                        const tipTpl = L.get('tooltip.money.rent')
                            || 'Rent: ${0}/week. Next due Day {1} ({2}).';
                        const when = daysUntil <= 0 ? (L.get('time.today') || 'today')
                            : daysUntil === 1 ? (L.get('time.tomorrow') || 'tomorrow')
                            : (L.get('time.in_n_days') || 'in {0} days').replace('{0}', daysUntil);
                        text += `\n${tipTpl.replace('{0}', rentAmount).replace('{1}', rentDueDay).replace('{2}', when)}`;
                    }
                } catch(e) {}

                return text;
            });
        }

        // Status icon tooltips
        const STATUS_TOOLTIP_KEYS = {
            'status-dirty': 'tooltip.status.dirty',
            'status-drunk': 'tooltip.status.drunk',
            'status-tired': 'tooltip.status.tired',
            'status-makeup': 'tooltip.status.makeup',
            'status-aroused': 'tooltip.status.aroused',
            'status-hrt-euphoric': 'tooltip.status.hrt_euphoric',
            'status-hrt-weepy': 'tooltip.status.hrt_weepy',
            'status-hrt-irritable': 'tooltip.status.hrt_irritable',
            'buff-chastity-fem': 'tooltip.buff.chastity_fem',
        };

        for (const [elemId, key] of Object.entries(STATUS_TOOLTIP_KEYS)) {
            const el = document.getElementById(elemId);
            if (el) this._addTooltip(el, () => L.get(key));
        }
    }

    _addTooltip(el, textFn) {
        let tip = null;
        el.addEventListener('mouseenter', () => {
            const text = textFn();
            if (!text || text.startsWith('tooltip.')) return; // key not found
            tip = document.createElement('div');
            tip.className = 'stat-tooltip';
            tip.textContent = text;
            document.body.appendChild(tip);

            const rect = el.getBoundingClientRect();
            let left = rect.left + rect.width / 2;
            let top = rect.bottom + 6;

            // Clamp to viewport so tooltip doesn't overflow
            const tipRect = tip.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            // Horizontal: keep within viewport with 8px margin
            if (left - tipRect.width / 2 < 8) left = tipRect.width / 2 + 8;
            if (left + tipRect.width / 2 > vw - 8) left = vw - tipRect.width / 2 - 8;
            // Vertical: if overflowing bottom, show above element instead
            if (top + tipRect.height > vh - 8) {
                top = rect.top - tipRect.height - 6;
            }

            tip.style.left = `${left}px`;
            tip.style.top = `${top}px`;
        });
        el.addEventListener('mouseleave', () => {
            if (tip) { tip.remove(); tip = null; }
        });
    }

    // ------------------------------------------------------------------
    // Inline Stat Popup (floating near story area)
    // ------------------------------------------------------------------

    _showInlineStatPopup(statName, delta, yOffset = 0) {
        const POPUP_CSS_MAP = {
            [Stats.ENERGY]: 'popup-energy',
            [Stats.AROUSAL]: 'popup-arousal',
            [Stats.FEMININITY]: 'popup-fem',
            [Stats.SUBMISSIVENESS]: 'popup-sub',
            [Stats.MONEY]: 'popup-money',
            [Stats.SHAME]: 'popup-shame',
        };

        const sign = delta > 0 ? '+' : '';
        const displayName = this._getDisplayName(statName);
        const colorClass = POPUP_CSS_MAP[statName] || '';

        const popup = document.createElement('div');
        popup.className = `inline-stat-popup ${colorClass}`;
        popup.textContent = `${sign}${delta} ${displayName}`;
        if (yOffset) {
            popup.style.top = `calc(40% + ${yOffset}px)`;
        }
        document.body.appendChild(popup);

        gsap.fromTo(popup,
            { y: 0, opacity: 1 },
            { y: -40, opacity: 0, duration: 1.4, ease: 'power2.out', delay: 0.2, onComplete: () => popup.remove() }
        );
    }

    // ------------------------------------------------------------------
    // Day Transition Overlay
    // ------------------------------------------------------------------

    _showDayTransition(day) {
        const overlay = document.createElement('div');
        overlay.className = 'day-transition';
        const text = document.createElement('div');
        text.className = 'day-transition-text';
        const tpl = L.get('desktop.day') || 'Day {0}';
        text.textContent = tpl.replace('{0}', day);
        overlay.appendChild(text);
        document.body.appendChild(overlay);

        const tl = gsap.timeline({ onComplete: () => overlay.remove() });
        tl.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' })
          .fromTo(text, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: 'back.out(1.5)' }, '<0.1')
          .to(overlay, { opacity: 0, duration: 0.5, ease: 'power2.in', delay: 1 });
    }

    // ------------------------------------------------------------------
    // Helpers
    // ------------------------------------------------------------------

    _setBar(id, percent) {
        const el = this._elements[id];
        if (!el) return;
        const pct = Math.max(0, Math.min(100, percent));
        if (typeof gsap !== 'undefined') {
            gsap.to(el, { width: pct + '%', duration: 0.8, ease: 'power2.out' });
        } else {
            el.style.transition = 'width 0.8s ease';
            el.style.width = pct + '%';
        }
    }

    /**
     * Force-sync ALL stats from Ink after story processing.
     * Unblocks transient observer filter and applies final Ink values.
     * During processing, Ink transiently passes through code that changes
     * Energy/Money/TimeSlot/Day — only the FINAL value matters.
     */
    _forceSyncAllFromInk() {
        this._blockStatUpdates = false;
        const story = window._sm?.currentStory;
        if (!story) return;
        const vars = window._vars;

        // Sync numeric stats — read final Ink value, fix variableManager + UI
        for (const key of ['Energy', 'Arousal', 'Femininity', 'Submissiveness', 'Money', 'Shame']) {
            const inkVal = story.variablesState[key];
            if (inkVal === undefined) continue;
            this.values[key] = inkVal;
            // Also fix variableManager.baseVar if it was corrupted by transient observer
            if (vars && vars.baseVar) {
                const buff = vars.buffedVar?.get(key) || 0;
                vars.baseVar.set(key, Math.max(0, inkVal - buff));
            }
        }
        // Sync TimeSlot + Day (CRITICAL: also to baseVar — otherwise saves capture stale Day=1)
        const ts = story.variablesState['TimeSlot'];
        const day = story.variablesState['Day'];
        if (ts !== undefined) {
            this._updateTimeSlot(ts);
            if (vars && vars.baseVar) vars.baseVar.set('TimeSlot', ts);
        }
        if (day !== undefined) {
            this._updateDay(day);
            if (vars && vars.baseVar) vars.baseVar.set('Day', day);
        }
        // Sync status flags (dirty, tired, drunk, makeup, aroused)
        for (const [flag, elemId] of Object.entries(STATUS_FLAG_MAP)) {
            const val = story.variablesState[flag];
            const el = this._elements[elemId] || document.getElementById(elemId);
            if (el) el.classList.toggle('hidden', !val);
        }

        // Re-render all bars with final values
        this._renderAll();
    }

    _setText(id, value) {
        const el = this._elements[id];
        if (el) el.textContent = value;
    }

    _getDisplayName(statName) {
        const map = {
            Energy: 'Energy',
            Arousal: 'Arousal',
            Femininity: 'Femininity',
            Submissiveness: 'Submissiveness',
            Money: 'Money',
            Shame: 'Shame',
        };
        return map[statName] || statName;
    }

    _getBarIdForStat(statName) {
        const map = {
            Energy: 'full-bar-energy',
            Arousal: 'full-bar-arousal',
            Femininity: 'full-bar-fem',
            Submissiveness: 'full-bar-sub',
        };
        return map[statName] || null;
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        if (this._moneyTickTimer) {
            clearInterval(this._moneyTickTimer);
            this._moneyTickTimer = null;
        }
        if (this._notifFlushTimer) {
            clearTimeout(this._notifFlushTimer);
            this._notifFlushTimer = null;
        }
        if (this._popoverDocClick) {
            document.removeEventListener('click', this._popoverDocClick);
            this._popoverDocClick = null;
        }
        if (this._popoverKeydown) {
            document.removeEventListener('keydown', this._popoverKeydown);
            this._popoverKeydown = null;
        }
        if (this._popoverResizeClose) {
            window.removeEventListener('resize', this._popoverResizeClose);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this._popoverResizeClose);
            }
            this._popoverResizeClose = null;
        }
        this._bound = false;
    }
}

export const statsUI = new StatsUI();
